import { Fragment, useMemo, useState } from "react";
import { Button, OverlayTrigger, Popover, Table } from "react-bootstrap";
import {
  Check,
  ChevronDown,
  ChevronRight,
  InfoCircle,
  JournalText,
  Pencil,
} from "react-bootstrap-icons";
import { Link } from "react-router-dom";
import { Currency } from "../../../../lib/components/Currency";
import { FormattedDateTime } from "../../../../lib/components/FormattedDate";
import { EmptyRow } from "./EmptyState";
import { RecordActions } from "./RecordActions";
import {
  buildChargebackSelectionRow,
  getChargebackAmount,
  getChargebackSelectionKey,
  isChargebackSelectable,
} from "../utils/recordSelection";

const COL_COUNT = 7;

function statusClass(status) {
  if (status === "approved") return "status-text status-text--approved";
  if (status === "paid") return "status-text status-text--paid";
  if (status === "pending" || status === "in_review")
    return "status-text status-text--pending";
  if (status === "rejected" || status === "declined")
    return "status-text status-text--rejected";
  return "status-text";
}

export function ChargebacksSubTable({
  records,
  agentKey,
  onAdjust,
  onReject,
  onApprove,
  onRemove,
  onReopen,
  selectedKeys,
  onToggleSelect,
  onToggleSelectAll,
  isSelectionDisabled,
  areRecordActionsDisabled,
  approvingRecordKeys,
  removingRecordKeys,
}) {
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const selectableRows = useMemo(
    () =>
      records
        .map((record, index) =>
          buildChargebackSelectionRow(agentKey, record, index),
        )
        .filter((row) => row.selectable),
    [agentKey, records],
  );
  const selectableCount = selectableRows.length;
  const selectedCount = selectableRows.filter((row) =>
    selectedKeys.has(row.key),
  ).length;
  const allSelected = selectableCount > 0 && selectedCount === selectableCount;
  const someSelected = selectedCount > 0 && !allSelected;

  const toggleAll = () => onToggleSelectAll(selectableRows, !allSelected);

  const toggleRow = (rowKey) => {
    setExpandedRows((previous) => {
      const next = new Set(previous);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  };

  return (
    <Table
      hover
      responsive
      className="data-table data-table--compact data-table--interactive align-middle"
      striped
    >
      {records.length > 0 && (
        <thead>
          <tr>
            <th className="col-select" aria-label="Select all">
              {selectableCount > 0 && (
                <button
                  type="button"
                  role="checkbox"
                  className={`record-select-checkbox ${allSelected || someSelected ? "is-on" : ""}`.trim()}
                  aria-label="Select all chargeback rows"
                  aria-checked={someSelected ? "mixed" : allSelected}
                  onClick={toggleAll}
                  disabled={isSelectionDisabled}
                >
                  {someSelected ? (
                    <span className="record-select-dash" />
                  ) : allSelected ? (
                    <Check size={11} strokeWidth={3.2} />
                  ) : null}
                </button>
              )}
            </th>
            <th className="col-toggle" aria-label="Details" />
            <th>Provider #</th>
            <th>Contract #</th>
            <th>Status</th>
            <th className="col-money">Amount</th>
            <th className="col-actions col-sticky">Actions</th>
          </tr>
        </thead>
      )}
      <tbody>
        {records.length === 0 ? (
          <EmptyRow colSpan={COL_COUNT} text="No chargeback records." />
        ) : (
          records.map((record, index) => {
            const id = record.chargebackRecordId ?? index;
            const rowKey = `chargeback-${id}`;
            const selectionKey = getChargebackSelectionKey(
              agentKey,
              record,
              index,
            );
            const isSelected = selectedKeys.has(selectionKey);
            const isSelectable = isChargebackSelectable(record);
            const adjustments = record.adjustments ?? [];
            const hasDetails =
              adjustments.length > 0 || Boolean(record.notes?.trim());
            const isExpanded = expandedRows.has(rowKey);
            const label = "Chargeback";
            const actionKey = `chargeback:${id}`;
            const originalAmount = record.amount ?? 0;
            const adjustmentTotal = (record.adjustments ?? []).reduce(
              (total, adjustment) =>
                total +
                (adjustment.adjustmentAmount ?? adjustment.adjustedAmount ?? 0),
              0,
            );
            const amount = getChargebackAmount(record);
            const showAmountTooltip =
              adjustments.length > 0 || adjustmentTotal !== 0;
            const popoverId = `chargeback-amount-breakdown-${id}`;

            return (
              <Fragment key={rowKey}>
                <tr>
                  <td className="col-select">
                    <button
                      type="button"
                      role="checkbox"
                      className={`record-select-checkbox ${isSelected ? "is-on" : ""} ${!isSelectable ? "is-disabled" : ""}`.trim()}
                      aria-label={`Select chargeback ${label}`}
                      aria-checked={isSelected}
                      onClick={() =>
                        onToggleSelect(
                          buildChargebackSelectionRow(agentKey, record, index),
                        )
                      }
                      disabled={!isSelectable || isSelectionDisabled}
                    >
                      {isSelected ? (
                        <Check size={11} strokeWidth={3.2} />
                      ) : null}
                    </button>
                  </td>
                  <td className="col-toggle">
                    {hasDetails && (
                      <Button
                        variant="link"
                        className="btn-toggle"
                        onClick={() => toggleRow(rowKey)}
                        aria-label={
                          isExpanded ? "Collapse details" : "Expand details"
                        }
                      >
                        {isExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                      </Button>
                    )}
                  </td>
                  <td>
                    {record.contractNumber && record.providerNumber ? (
                      <Link
                        to={`/contracts/${record.contractNumber}`}
                        target="_blank"
                      >
                        {record.providerNumber}
                      </Link>
                    ) : (
                      record.providerNumber || "-"
                    )}
                  </td>
                  <td>
                    {record.contractNumber ? (
                      <Link
                        to={`/contracts/${record.contractNumber}`}
                        target="_blank"
                      >
                        {record.contractNumber}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <span className={statusClass(record.status)}>
                      {record.status || "-"}
                    </span>
                  </td>
                  <td className="col-money tnum col-money--danger col-money--emphasis">
                    {showAmountTooltip ? (
                      <span className="bonus-amount-wrap">
                        <Currency amount={amount} />
                        <OverlayTrigger
                          placement="top"
                          trigger={["hover", "focus"]}
                          overlay={
                            <Popover
                              id={popoverId}
                              className="bonus-amount-popover"
                            >
                              <Popover.Body>
                                <div className="bonus-amount-tooltip-grid">
                                  <span>Original</span>
                                  <span>
                                    <Currency amount={originalAmount} />
                                  </span>
                                  <span>Adjustments</span>
                                  <span>
                                    <Currency amount={adjustmentTotal} />
                                  </span>
                                </div>
                                <div className="bonus-amount-tooltip-divider" />
                                <div className="bonus-amount-tooltip-grid bonus-amount-tooltip-total">
                                  <span>Total</span>
                                  <span>
                                    <Currency amount={amount} />
                                  </span>
                                </div>
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <button
                            type="button"
                            className="bonus-amount-trigger"
                            aria-label="Show chargeback amount breakdown"
                          >
                            <InfoCircle size={13} aria-hidden="true" />
                          </button>
                        </OverlayTrigger>
                      </span>
                    ) : (
                      <span className="bonus-amount-wrap">
                        <Currency amount={amount} />
                      </span>
                    )}
                  </td>
                  <td className="col-actions col-sticky">
                    <RecordActions
                      isFinalized={
                        record.status === "approved" ||
                        record.status === "paid" ||
                        record.status === "declined"
                      }
                      isApproving={approvingRecordKeys.has(actionKey)}
                      isRemoving={removingRecordKeys.has(actionKey)}
                      isDisabled={areRecordActionsDisabled}
                      onApprove={
                        onApprove
                          ? () =>
                              onApprove({
                                kind: "chargeback",
                                id: record.chargebackRecordId,
                                label,
                              })
                          : undefined
                      }
                      onAdjust={() =>
                        onAdjust({
                          kind: "chargeback",
                          id: record.chargebackRecordId,
                          label,
                          effective: amount,
                        })
                      }
                      onReject={() =>
                        onReject({
                          kind: "chargeback",
                          id: record.chargebackRecordId,
                          label,
                        })
                      }
                      onRemove={
                        onRemove
                          ? () =>
                              onRemove({
                                kind: "chargeback",
                                id: record.chargebackRecordId,
                                label,
                              })
                          : undefined
                      }
                      onReopen={
                        onReopen
                          ? () =>
                              onReopen({
                                kind: "chargeback",
                                id: record.chargebackRecordId,
                                label,
                              })
                          : undefined
                      }
                    />
                  </td>
                </tr>
                {isExpanded && hasDetails && (
                  <ChargebackDetailsRow
                    colSpan={COL_COUNT}
                    adjustments={adjustments}
                    notes={record.notes}
                  />
                )}
              </Fragment>
            );
          })
        )}
      </tbody>
    </Table>
  );
}

function ChargebackDetailsRow({ colSpan, adjustments, notes }) {
  return (
    <tr className="details-row">
      <td colSpan={colSpan}>
        {adjustments.length > 0 && (
          <DetailSection
            title="Chargeback adjustments"
            icon={<Pencil size={12} aria-hidden="true" />}
          >
            <Table
              responsive
              className="data-table data-table--nested align-middle"
            >
              <thead>
                <tr>
                  <th>User</th>
                  <th>Date</th>
                  <th>Reason</th>
                  <th className="col-money">Amount</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((adjustment, index) => (
                  <tr
                    key={
                      adjustment.chargebackRecordAdjustmentId ??
                      adjustment.created ??
                      index
                    }
                  >
                    <td>{adjustment.createdBy || "-"}</td>
                    <td>
                      {adjustment.created ? (
                        <FormattedDateTime date={adjustment.created} />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{adjustment.reason || "-"}</td>
                    <td className="col-money tnum col-money--emphasis">
                      <Currency
                        amount={
                          adjustment.adjustmentAmount ??
                          adjustment.adjustedAmount ??
                          0
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </DetailSection>
        )}
        {notes && (
          <DetailSection
            title="Notes"
            icon={<JournalText size={12} aria-hidden="true" />}
          >
            <p>{notes}</p>
          </DetailSection>
        )}
      </td>
    </tr>
  );
}

function DetailSection({ title, icon, children }) {
  return (
    <div className="details-section">
      <div className="details-section-title">
        {icon && <span className="details-section-icon">{icon}</span>}
        {title}
      </div>
      {children}
    </div>
  );
}
